import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Parties from "@/pages/Parties";
import Requests from "@/pages/Requests";
import RequestDetail from "@/pages/RequestDetail";
import Quotes from "@/pages/Quotes";
import Tariffs from "@/pages/Tariffs";
import Operations from "@/pages/Operations";
import OperationDetail from "@/pages/OperationDetail";
import Reports from "@/pages/Reports";
import AuditLog from "@/pages/AuditLog";
import Users from "@/pages/Users";
import Portal from "@/pages/Portal";
import "@/index.css";

const Guard = ({ children, customerOnly = false, perm = null }) => {
  const { user, loading, can } = useAuth();
  if (loading)
    return <div className="flex h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-klein" /></div>;
  if (!user) return <Navigate to="/giris" replace />;
  if (customerOnly && user.role !== "customer") return <Navigate to="/panel" replace />;
  if (!customerOnly && user.role === "customer") return <Navigate to="/portal" replace />;
  if (perm && !can(perm)) return <Forbidden />;
  return children;
};

const Forbidden = () => (
  <div className="brut p-8" data-testid="forbidden-page">
    <p className="overline">Yetkisiz Erişim</p>
    <h1 className="mt-2 font-display text-3xl font-black tracking-tighter">Bu sayfaya erişim yetkiniz yok</h1>
    <p className="mt-3 text-sm text-zinc-600">Erişim gerekiyorsa sistem yöneticisi ile görüşün.</p>
  </div>
);

const Entry = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-klein" /></div>;
  if (!user) return <Navigate to="/giris" replace />;
  return <Navigate to={user.role === "customer" ? "/portal" : "/panel"} replace />;
};

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" />
        <Routes>
          <Route path="/giris" element={<Login />} />
          <Route path="/" element={<Entry />} />
          <Route path="/portal" element={<Guard customerOnly><Portal /></Guard>} />
          <Route element={<Guard><Layout /></Guard>}>
            <Route path="/panel" element={<Guard perm="operation:read"><Dashboard /></Guard>} />
            <Route path="/cariler" element={<Guard perm="party:read"><Parties /></Guard>} />
            <Route path="/talepler" element={<Guard perm="request:read"><Requests /></Guard>} />
            <Route path="/talepler/:id" element={<Guard perm="request:read"><RequestDetail /></Guard>} />
            <Route path="/teklifler" element={<Guard perm="quote:read"><Quotes /></Guard>} />
            <Route path="/tarifeler" element={<Guard perm="quote:read"><Tariffs /></Guard>} />
            <Route path="/operasyonlar" element={<Guard perm="operation:read"><Operations /></Guard>} />
            <Route path="/operasyonlar/:id" element={<Guard perm="operation:read"><OperationDetail /></Guard>} />
            <Route path="/raporlar" element={<Guard perm="report:read"><Reports /></Guard>} />
            <Route path="/denetim" element={<Guard perm="audit:read"><AuditLog /></Guard>} />
            <Route path="/kullanicilar" element={<Guard perm="*"><Users /></Guard>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
